import React from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useAuth } from "./contexts/AuthContext";
import { useEffect, useState } from "react";
import {
  AuthPage,
  AuthCallbackPage,
  LandingPage,
  GeneratorPage,
  TemplatesPage,
  TemplateEditorPage,
  PricingPage,
  ContactPage,
  DashboardPage,
  BlockPatternsPage,
  SettingsPage,
  PrivacyPolicyPage,
  TermsPage,
  DocumentationPage,
} from "./pages";
import {
  GettingStartedPage,
  AIGenerationPage,
  BlockTypesPage,
  TemplatesDocsPage,
  APIReferencePage,
  PatternFormatPage,
} from "./pages/docs";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setHasCheckedAuth(true);
      if (!user) {
        navigate("/auth?mode=signin");
      }
    }
  }, [user, isLoading, navigate]);

  if (isLoading || !hasCheckedAuth) {
    return (
      <div className="min-h-screen bg-volt-deep-blue flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-volt-blue border-t-transparent mb-4" />
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <>{children}</> : null;
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/generator" element={<GeneratorPage />} />
      <Route path="/templates" element={<TemplatesPage />} />
      <Route path="/templates/:id" element={<TemplateEditorPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/blocks"
        element={
          <ProtectedRoute>
            <BlockPatternsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/docs" element={<DocumentationPage />} />
      <Route path="/docs/getting-started" element={<GettingStartedPage />} />
      <Route path="/docs/ai-generation" element={<AIGenerationPage />} />
      <Route path="/docs/block-types" element={<BlockTypesPage />} />
      <Route path="/docs/templates" element={<TemplatesDocsPage />} />
      <Route path="/docs/api-reference" element={<APIReferencePage />} />
      <Route path="/docs/pattern-format" element={<PatternFormatPage />} />
    </Routes>
  );
}

export default App;
